import { createContext, useContext, useState, useEffect, useRef, type CSSProperties } from "react";

export type MotionLevel = 0 | 0.5 | 1 | 1.5;

export const MotionContext = createContext<MotionLevel>(1);
export const useMotion = () => useContext(MotionContext);

/** Staggered entry reveal — returns a style object to spread onto any element. */
export function useReveal(delay = 0, distance = 12): CSSProperties {
  const m = useMotion();
  const [shown, setShown] = useState(m === 0);
  useEffect(() => {
    if (m === 0) { setShown(true); return; }
    const id = setTimeout(() => setShown(true), delay * m);
    return () => clearTimeout(id);
  }, [m, delay]);
  return {
    opacity: shown ? 1 : 0,
    transform: shown ? "translateY(0)" : `translateY(${distance}px)`,
    transition: m === 0 ? "none" : `opacity ${0.5 * m}s cubic-bezier(.2,.7,.2,1), transform ${0.55 * m}s cubic-bezier(.2,.7,.2,1)`,
    willChange: "opacity, transform",
  };
}

/** Animate a number from 0 → target on mount. Returns current displayed value. */
export function useCountUp(target: number, { delay = 0, duration = 1200 } = {}) {
  const m = useMotion();
  const [v, setV] = useState(m === 0 ? target : 0);
  useEffect(() => {
    if (m === 0) { setV(target); return; }
    let raf = 0;
    const dur = duration * m;
    const tStart = setTimeout(() => {
      let start = 0;
      const tick = (t: number) => {
        if (!start) start = t;
        const p = Math.min(1, (t - start) / dur);
        const e = 1 - Math.pow(1 - p, 3); // easeOutCubic
        setV(target * e);
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay * m);
    return () => { clearTimeout(tStart); cancelAnimationFrame(raf); };
  }, [target, delay, duration, m]);
  return Math.round(v);
}

/** Sine pulse 0→1→0. Use for live dots, breathing effects. Period in ms. */
export function usePulse(period = 2000) {
  const m = useMotion();
  const [v, setV] = useState(0);
  useEffect(() => {
    if (m === 0) { setV(0.5); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const phase = ((t - start) % period) / period;
      setV((Math.sin(phase * Math.PI * 2) + 1) / 2);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [m, period]);
  return v;
}
